import resolveResponse from 'contentful-resolve-response'

import {ThemeProvider, Box, Grid} from '@primer/react-brand'

import {useRoutePayload} from '@github-ui/react-core/use-route-payload'

import {ContentfulSubnav} from '@github-ui/swp-core/components/contentful/ContentfulSubnav'
import {ContentfulInlineFootnotesList} from '@github-ui/swp-core/components/contentful/ContentfulInlineFootnotesList'
import {FootnotesProvider} from '@github-ui/swp-core/components/contentful/FootnotesContext'

import {ZodSilentErrorBoundary} from '../../../../components/ZodSilentErrorBoundary/ZodSilentErrorBoundary'
import {toPayload} from '../../../../lib/types/payload'
import {toBrandPage} from '../../../../brand/lib/types/contentful'
import type {GenericSectionWithIds} from '../../../../brand/lib/types/contentful'
import {cohortFunnelBuilder} from '../../../../lib/analytics'

import {FaqSection} from '../_sections/FaqSection/FaqSection'

import PricingBackground from '../_assets/pricing-bg.webp'
import PricingBackgroundSm from '../_assets/pricing-bg-sm.webp'

import {PlanTypeContextProvider} from '../_context/PlanTypeContext'

import {CompareTable} from './CompareTable'
import {CtaSection} from './CtaSection'
import {HeroSection} from './HeroSection'

import SmallIdeSection from '../_components/SmallIdeSection'
import {LegacyPricingPlans} from '../_components/PricingPlans/LegacyPricingPlans'
import LegacyHero from './LegacyHero'

export default function FeaturesCopilotPlansIndex() {
  const {contentfulRawJsonResponse} = toPayload(useRoutePayload<unknown>())
  const contentfulResponse = resolveResponse(contentfulRawJsonResponse)
  const page = toBrandPage(contentfulResponse)

  const {template} = page.fields
  const {subnav} = template.fields
  const {
    featuresCopilotFaqSection,
    featuresCopilotPlansComparison,
    featuresCopilotPlansCtaSection,
    featuresCopilotPlansHeroSection,
  } = page.ids as {
    featuresCopilotFaqSection: GenericSectionWithIds
    featuresCopilotPlansComparison: GenericSectionWithIds
    featuresCopilotPlansCtaSection: GenericSectionWithIds
    featuresCopilotPlansHeroSection: GenericSectionWithIds
  }

  let {copilotBusinessContactSalesPath} = useRoutePayload<{copilotBusinessContactSalesPath: string}>()
  let {copilotBusinessSignupPath} = useRoutePayload<{copilotBusinessSignupPath: string}>()
  let {copilotEnterpriseContactSalesPath} = useRoutePayload<{copilotEnterpriseContactSalesPath: string}>()
  let {copilotEnterpriseSignupPath} = useRoutePayload<{copilotEnterpriseSignupPath: string}>()
  let {copilotProPlusSignupPath} = useRoutePayload<{copilotProPlusSignupPath: string}>()
  let {copilotProSignupPath} = useRoutePayload<{copilotProSignupPath: string}>()
  const {cft} = useRoutePayload<{cft: string}>()

  const withCft = cohortFunnelBuilder(cft)

  copilotBusinessContactSalesPath = withCft(copilotBusinessContactSalesPath)
  copilotBusinessSignupPath = withCft(copilotBusinessSignupPath, {product: 'cfb'})
  copilotEnterpriseContactSalesPath = withCft(copilotEnterpriseContactSalesPath)
  copilotEnterpriseSignupPath = withCft(copilotEnterpriseSignupPath, {product: 'ce'})
  copilotProSignupPath = withCft(copilotProSignupPath, {product: 'cfi'})
  copilotProPlusSignupPath = withCft(copilotProPlusSignupPath, {product: 'cpp'})

  return (
    <ZodSilentErrorBoundary>
      <FootnotesProvider>
        <PlanTypeContextProvider>
          <ThemeProvider colorMode="dark" className="lp-Copilot position-relative">
            <Box className="lp-SubNav-spacer" />

            {subnav ? (
              <ContentfulSubnav component={subnav} className="lp-Hero-subnav lp-Hero-subnav--highContrast" />
            ) : null}

            <picture>
              <source srcSet={PricingBackgroundSm} media="(max-width: 767px)" />
              <img src={PricingBackground} className="position-absolute top-0 left-0 width-full height-auto" alt="" />
            </picture>

            {featuresCopilotPlansHeroSection ? (
              <HeroSection contentfulContent={featuresCopilotPlansHeroSection} />
            ) : (
              <>
                <LegacyHero />

                <LegacyPricingPlans
                  copilotBusinessContactSalesPath={copilotBusinessContactSalesPath}
                  copilotBusinessSignupPath={copilotBusinessSignupPath}
                  copilotEnterpriseContactSalesPath={copilotEnterpriseContactSalesPath}
                  copilotEnterpriseSignupPath={copilotEnterpriseSignupPath}
                  copilotProPlusSignupPath={copilotProPlusSignupPath}
                  copilotProSignupPath={copilotProSignupPath}
                />

                <SmallIdeSection />
              </>
            )}

            {featuresCopilotPlansComparison ? (
              <CompareTable contentfulContent={featuresCopilotPlansComparison} />
            ) : null}

            {featuresCopilotPlansCtaSection ? <CtaSection contentfulContent={featuresCopilotPlansCtaSection} /> : null}

            {featuresCopilotFaqSection ? <FaqSection contentfulContent={featuresCopilotFaqSection} /> : null}

            <Grid>
              <Grid.Column span={12}>
                <ContentfulInlineFootnotesList className="pt-0" />
              </Grid.Column>
            </Grid>
          </ThemeProvider>
        </PlanTypeContextProvider>
      </FootnotesProvider>
    </ZodSilentErrorBoundary>
  )
}

try{ FeaturesCopilotPlansIndex.displayName ||= 'FeaturesCopilotPlansIndex' } catch {}